import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const emptyForm = { name: '', notes: '', assemblyRows: [{ assembly_product_id: '', quantity: 1 }] };

const statusLabels = { draft: 'Taslak', quoting: 'Teklif Alınıyor', done: 'Tamamlandı' };

export default function SourcingProjects() {
  const [projects, setProjects] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchProjects = async () => {
    const res = await api.get('/api/sourcing/projects');
    setProjects(res.data);
  };

  const fetchAssemblies = async () => {
    const res = await api.get('/api/bom/assemblies');
    setAssemblies(res.data);
  };

  useEffect(() => {
    fetchProjects();
    fetchAssemblies();
  }, []);

  const updateRow = (index, field, value) => {
    const rows = [...form.assemblyRows];
    rows[index] = { ...rows[index], [field]: value };
    setForm({ ...form, assemblyRows: rows });
  };

  const addRow = () => {
    setForm({ ...form, assemblyRows: [...form.assemblyRows, { assembly_product_id: '', quantity: 1 }] });
  };

  const removeRow = (index) => {
    setForm({ ...form, assemblyRows: form.assemblyRows.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const assembliesPayload = form.assemblyRows.map(r => ({
        assembly_product_id: parseInt(r.assembly_product_id),
        quantity: parseFloat(r.quantity),
      }));
      await api.post('/api/sourcing/projects', { name: form.name, notes: form.notes || null, assemblies: assembliesPayload });
      setShowForm(false);
      setForm(emptyForm);
      fetchProjects();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Tedarik Projeleri</h2>
        <button onClick={() => { setForm(emptyForm); setShowForm(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">
          + Yeni Proje
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Yeni Tedarik Projesi</h3>
            <form onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proje Adı *</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Montajlar *</label>
                <div className="space-y-2">
                  {form.assemblyRows.map((row, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <select required value={row.assembly_product_id} onChange={e => updateRow(i, 'assembly_product_id', e.target.value)} className="flex-1 border rounded px-3 py-2 text-sm">
                        <option value="">Montaj seçin</option>
                        {assemblies.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                      </select>
                      <input required type="number" step="0.001" min="0.001" value={row.quantity} onChange={e => updateRow(i, 'quantity', e.target.value)} className="w-28 border rounded px-3 py-2 text-sm" placeholder="Miktar" />
                      {form.assemblyRows.length > 1 && (
                        <button type="button" onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700 text-sm font-medium px-2">Çıkar</button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addRow} className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">+ Montaj Ekle</button>
              </div>

              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium">Oluştur</button>
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Proje Adı</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Montaj Sayısı</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Kalem Sayısı</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Durum</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {projects.map(p => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{p.name}</td>
                <td className="px-4 py-3 text-right text-gray-600">{p.assembly_count}</td>
                <td className="px-4 py-3 text-right text-gray-600">{p.item_count}</td>
                <td className="px-4 py-3">
                  <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded">{statusLabels[p.status] || p.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/sourcing/${p.id}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm">Detay</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {projects.length === 0 && (
          <div className="text-center py-12 text-gray-400">Henüz tedarik projesi eklenmemiş</div>
        )}
      </div>
    </div>
  );
}
